import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import confetti from "canvas-confetti";
import { Trophy, Star, X } from "lucide-react";
import { API } from "../context";
import { getActiveLearnerId } from "../hooks/useActiveLearner";
import { STARS_EARNED_EVENT } from "../lib/starEvents";

// Global, app-wide Level-Up celebration. Mounts once (in App.js) and watches the
// ACTIVE learner's level. The instant a learner crosses a Star threshold and
// levels up — anywhere Stars are earned (arcade, lessons, gifts, quizzes) — it
// pours confetti and shows a "You reached Level N!" moment.
//
// It compares the live level against the last level we celebrated (stored per
// learner in localStorage) so a page reload never re-fires, and the first-ever
// fetch just sets a silent baseline.
const seenKey = (uid) => `iw_level_seen::${uid}`;

function fireConfetti() {
  const shots = [
    { spread: 90, startVelocity: 45, particleCount: 60, origin: { x: 0.5, y: 0.35 } },
    { spread: 120, startVelocity: 30, particleCount: 40, origin: { x: 0.2, y: 0.4 } },
    { spread: 120, startVelocity: 30, particleCount: 40, origin: { x: 0.8, y: 0.4 } },
  ];
  const colors = ["#FFD700", "#FF6B9D", "#7C5CFC", "#34D399", "#38BDF8"];
  shots.forEach((s, i) => setTimeout(() => {
    try { confetti({ ...s, colors, ticks: 260, zIndex: 2147483500, disableForReducedMotion: true }); } catch { /* noop */ }
  }, i * 180));
}

export default function LevelUpCelebration({ user }) {
  const [celebrate, setCelebrate] = useState(null); // {level, name}
  const lastLevel = useRef(null);
  const checkTimer = useRef(null);
  const busy = useRef(false);

  const selfId = user?.profile_id || user?.id || null;

  const check = useCallback(async () => {
    const uid = getActiveLearnerId(selfId);
    if (!uid || busy.current) return;
    busy.current = true;
    try {
      const { data } = await axios.get(`${API}/arcade/progression/${encodeURIComponent(uid)}`);
      const level = Number(data?.level || 1);
      const name = data?.level_name || "";
      const isOverride = !!data?.level_is_override;
      let seen = lastLevel.current;
      if (seen == null) {
        try { const s = localStorage.getItem(seenKey(uid)); if (s != null) seen = Number(s); } catch { /* ignore */ }
      }
      // First time we ever see this learner: set a silent baseline (no party).
      if (seen == null) {
        lastLevel.current = level;
        try { localStorage.setItem(seenKey(uid), String(level)); } catch { /* ignore */ }
        return;
      }
      if (level > seen) {
        lastLevel.current = level;
        try { localStorage.setItem(seenKey(uid), String(level)); } catch { /* ignore */ }
        // Skip the party for an admin/command level override — that's a manual set,
        // not an earned level-up. Real earned/gifted levels are never overrides.
        if (!isOverride) {
          fireConfetti();
          setCelebrate({ level, name });
        }
      } else if (level < seen) {
        // Level went down (e.g. an admin cleared an override) — resync silently.
        lastLevel.current = level;
        try { localStorage.setItem(seenKey(uid), String(level)); } catch { /* ignore */ }
      }
    } catch { /* offline / not ready — try again next tick */ }
    finally { busy.current = false; }
  }, [selfId]);

  // Debounced re-check shortly after Stars are earned (let the backend commit).
  const scheduleCheck = useCallback(() => {
    if (checkTimer.current) clearTimeout(checkTimer.current);
    checkTimer.current = setTimeout(check, 1200);
  }, [check]);

  useEffect(() => {
    if (!user) return undefined;
    // Reset baseline when the active learner changes so we track the right one.
    const onLearnerChange = () => { lastLevel.current = null; check(); };
    check();
    window.addEventListener(STARS_EARNED_EVENT, scheduleCheck);
    window.addEventListener("active-learner-changed", onLearnerChange);
    window.addEventListener("focus", check);
    const poll = setInterval(check, 25000);
    return () => {
      window.removeEventListener(STARS_EARNED_EVENT, scheduleCheck);
      window.removeEventListener("active-learner-changed", onLearnerChange);
      window.removeEventListener("focus", check);
      clearInterval(poll);
      if (checkTimer.current) clearTimeout(checkTimer.current);
    };
  }, [user, check, scheduleCheck]);

  if (!celebrate) return null;

  return createPortal(
    <div className="fixed inset-0 z-[2147483500] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]"
      onClick={() => setCelebrate(null)} data-testid="levelup-celebration">
      <div className="relative w-full max-w-sm rounded-3xl bg-gradient-to-b from-[#171e42] to-[#0e1330] border border-yellow-300/30 shadow-2xl p-6 text-center animate-[popIn_0.35s_cubic-bezier(0.34,1.56,0.64,1)]"
        onClick={(e) => e.stopPropagation()}>
        <button onClick={() => setCelebrate(null)} className="absolute top-3 right-3 text-white/50 hover:text-white" data-testid="levelup-close" aria-label="Close">
          <X size={20} />
        </button>
        <div className="mx-auto mb-3 w-20 h-20 rounded-full bg-yellow-400/15 border-2 border-yellow-300/50 flex items-center justify-center">
          <Trophy size={40} className="text-yellow-300" />
        </div>
        <p className="text-yellow-300 font-fredoka font-bold text-sm tracking-wide uppercase mb-1">Level Up!</p>
        <h2 className="text-white font-fredoka font-extrabold text-3xl mb-1" data-testid="levelup-title">
          You reached Level {celebrate.level}!
        </h2>
        {celebrate.name && (
          <p className="text-white/70 font-nunito text-base mb-4 flex items-center justify-center gap-1.5">
            <Star size={14} className="text-yellow-300" fill="currentColor" /> {celebrate.name}
          </p>
        )}
        <button onClick={() => setCelebrate(null)}
          className="mt-2 w-full py-3 rounded-2xl bg-yellow-400 text-blue-900 font-fredoka font-bold text-base active:scale-95 transition-transform"
          data-testid="levelup-continue">
          Keep going! 🚀
        </button>
      </div>
    </div>,
    document.body
  );
}
